import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { ArrowLeft, Brain, Shuffle, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudyPage() {
    const { setId } = useParams();
    const navigate = useNavigate();
    const [flashcardSet, setFlashcardSet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchSet();
    }, [setId]);

    const fetchSet = async () => {
        try {
            const response = await axios.get(`${API}/sets/${setId}`);
            setFlashcardSet(response.data);
        } catch (err) {
            setError('Failed to load set');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="pt-24 flex items-center justify-center min-h-[80vh]">
                    <div className="animate-pulse text-muted-foreground">Loading...</div>
                </div>
            </div>
        );
    }

    if (error || !flashcardSet) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="pt-24 flex flex-col items-center justify-center min-h-[80vh]">
                    <p className="text-destructive mb-4">{error || 'Set not found'}</p>
                    <Button onClick={() => navigate('/dashboard')} className="rounded-none">
                        Back to Dashboard
                    </Button>
                </div>
            </div>
        );
    }

    if (flashcardSet.card_count < 2) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="pt-24 flex flex-col items-center justify-center min-h-[80vh] px-6">
                    <Brain className="w-16 h-16 text-muted-foreground mb-6" strokeWidth={1} />
                    <h2 className="font-serif text-2xl mb-2">Not enough cards</h2>
                    <p className="text-muted-foreground mb-6 text-center">
                        You need at least 2 flashcards to start studying.<br />
                        This set has {flashcardSet.card_count} card{flashcardSet.card_count !== 1 ? 's' : ''}.
                    </p>
                    <Button onClick={() => navigate(`/edit/${setId}`)} className="rounded-none">
                        Add more cards
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <Header />

            <main className="pt-24 pb-16 px-6">
                <div className="max-w-2xl mx-auto">
                    {/* Back Button */}
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/dashboard')}
                        data-testid="back-button"
                        className="rounded-none mb-8"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Back
                    </Button>

                    {/* Set Title */}
                    <div className="mb-12 text-center">
                        <h1 
                            data-testid="study-set-title"
                            className="font-serif text-3xl md:text-4xl tracking-tight mb-2"
                        >
                            {flashcardSet.title}
                        </h1>
                        <p className="font-mono text-xs tracking-wider text-muted-foreground uppercase">
                            {flashcardSet.card_count} cards
                        </p>
                    </div>

                    {/* Study Mode Selection */}
                    <div className="space-y-6">
                        <h2 className="font-mono text-xs tracking-widest uppercase text-muted-foreground text-center mb-8">
                            Choose Study Mode
                        </h2>

                        <Button
                            onClick={() => navigate(`/study/${setId}/learn`)}
                            data-testid="learn-mode-button"
                            variant="outline"
                            className="w-full h-24 rounded-none border-2 btn-lift flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-primary/5"
                        >
                            <Brain className="w-6 h-6" strokeWidth={1.5} />
                            <div>
                                <span className="font-serif text-lg">Learn Mode</span>
                                <p className="text-xs text-muted-foreground mt-1">Type your answer with typo tolerance</p>
                            </div>
                        </Button>

                        <Button
                            onClick={() => navigate(`/study/${setId}/multiple-choice`)}
                            data-testid="multiple-choice-button"
                            variant="outline"
                            className="w-full h-24 rounded-none border-2 btn-lift flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-primary/5"
                        >
                            <Shuffle className="w-6 h-6" strokeWidth={1.5} />
                            <div>
                                <span className="font-serif text-lg">Multiple Choice</span>
                                <p className="text-xs text-muted-foreground mt-1">Pick from 4 options</p>
                            </div>
                        </Button>

                        <Button
                            onClick={() => navigate(`/study/${setId}/flashcards`)}
                            data-testid="flashcard-mode-button"
                            variant="outline"
                            className="w-full h-24 rounded-none border-2 btn-lift flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-primary/5"
                        >
                            <RotateCcw className="w-6 h-6" strokeWidth={1.5} />
                            <div>
                                <span className="font-serif text-lg">Flashcard Review</span>
                                <p className="text-xs text-muted-foreground mt-1">Flip through cards at your pace</p>
                            </div>
                        </Button>
                    </div>
                </div>
            </main>
        </div>
    );
}
